import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  MAX_EXPAND_EVENTS,
  MAX_FOLD_EVENTS,
  MAX_MERGE_EVENTS,
} from '../../../../config/constants';
import { colors, layout } from '../../../../config/styles';
import { useAppSelector } from '../../../../redux/hooks';
import { IRowMeta, RowType } from '../../../../redux/slices/oldBuilder';
import { useDndEntityInternalContext } from '../../../DndEntity';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import useQueryBuilderDroppable from '../../../DndEntity/useQueryBuilderDroppable';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';

const DropzoneContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  border-radius: ${layout.containerBorderRadius}px;
`;

const DropzoneArea = styled.div`
  width: 100%;
  height: 32px;
  background: white;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${colors.hoverGreen};
    opacity: 1;
  }
`;

const EventsExpansionDropzone = () => {
  const { activeItem } = useDndEntityInternalContext();
  const { isBasicBuilderComplete } = useQueryBuilderDroppable();

  const expandEvents =
    useAppSelector(({ builder }) => builder[RowType.expandEvents]) ?? [];
  const mergeEvents =
    useAppSelector(({ builder }) => builder[RowType.mergeEvents]) ?? [];
  const foldEvents =
    useAppSelector(({ builder }) => builder[RowType.foldEvents]) ?? [];

  const allowAddExpand = expandEvents?.length < MAX_EXPAND_EVENTS;
  const allowAddMerge = mergeEvents?.length < MAX_MERGE_EVENTS;
  const allowAddFold = foldEvents?.length < MAX_FOLD_EVENTS;

  const isBasicEvent = useMemo(() => {
    const data = getDndActiveItemData(activeItem);
    if (data) {
      return data.__typename === 'TagLeafBasicEvent';
    }

    return false;
  }, [activeItem]);

  const isBasicAndVirtualEvent = useMemo(() => {
    const data = getDndActiveItemData(activeItem);
    if (data) {
      return (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      );
    }
    return false;
  }, [activeItem]);

  const showExpandDropzone =
    isBasicEvent && allowAddExpand && isBasicBuilderComplete;
  const showMergeDropzone =
    isBasicAndVirtualEvent && allowAddMerge && isBasicBuilderComplete;
  const showFoldDropzone =
    isBasicAndVirtualEvent && allowAddFold && isBasicBuilderComplete;

  const expandRowMeta: IRowMeta = {
    rowType: RowType.expandEvents,
    rowIndex: expandEvents.length ?? 0,
  };

  const mergeRowMeta: IRowMeta = {
    rowType: RowType.mergeEvents,
    rowIndex: mergeEvents.length ?? 0,
    eventIndex: 0,
  };

  const foldRowMeta: IRowMeta = {
    rowType: RowType.foldEvents,
    rowIndex: foldEvents.length ?? 0,
  };

  const expandEventDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    expandRowMeta,
  );
  const mergeEventDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    mergeRowMeta,
  );
  const foldEventDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    foldRowMeta,
  );

  return (
    <DropzoneContainer>
      {showExpandDropzone && (
        <DndDroppableZone
          id={expandEventDropzoneId}
          accepts={[EntityDropzoneType.Events]}
          data={
            {
              id: expandEventDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: expandRowMeta,
              isNewRow: true,
            } satisfies IEventEntityDropzoneDataType
          }
          shouldFillContent
        >
          <DropzoneArea>+ Add Expand Event</DropzoneArea>
        </DndDroppableZone>
      )}

      {showMergeDropzone && (
        <DndDroppableZone
          id={mergeEventDropzoneId}
          accepts={[EntityDropzoneType.Events]}
          data={
            {
              id: mergeEventDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: mergeRowMeta,
              isNewRow: true,
            } satisfies IEventEntityDropzoneDataType
          }
          shouldFillContent
        >
          <DropzoneArea>+ Add Merge Event</DropzoneArea>
        </DndDroppableZone>
      )}

      {showFoldDropzone && (
        <DndDroppableZone
          id={foldEventDropzoneId}
          accepts={[EntityDropzoneType.Events]}
          data={
            {
              id: foldEventDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: foldRowMeta,
              isNewRow: true,
            } satisfies IEventEntityDropzoneDataType
          }
          shouldFillContent
        >
          <DropzoneArea>+ Add Fold Event</DropzoneArea>
        </DndDroppableZone>
      )}
    </DropzoneContainer>
  );
};

export default EventsExpansionDropzone;
